'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';

const floatingStats = [
  { label: 'Active Students', value: '24,800+', icon: 'UserGroupIcon', color: 'text-primary' },
  { label: 'Course Completion', value: '91%', icon: 'CheckBadgeIcon', color: 'text-success' },
  { label: 'Live Classes/Week', value: '140+', icon: 'VideoCameraIcon', color: 'text-accent' },
];

interface HeroSectionProps {
  heroImageSrc?: string;
  heroImageAlt?: string;
}

export default function HeroSection({
  heroImageSrc = '/assets/images/hero_african_student.png',
  heroImageAlt = 'Ghanaian student studying with laptop in a modern library — Nerdlora e-learning platform',
}: HeroSectionProps) {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <section className="relative pt-24 pb-16 overflow-hidden gradient-hero">
      {/* Background decorations */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-semibold">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse-subtle" />
              #1 E-Learning Platform in Ghana
            </div>

            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-extrabold text-foreground leading-tight">
              Learn Without{' '}
              <span className="text-gradient">Limits.</span>
              <br />
              Grow Without{' '}
              <span className="text-gradient">Borders.</span>
            </h1>

            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              From SHS revision to university-level and professional courses — Nerdlora brings world-class education to every Ghanaian student and professional. Learn at your pace, earn certificates, and build a career.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2 px-6 py-3 text-base font-semibold text-white gradient-purple rounded-xl hover:opacity-90 transition-all shadow-purple active:scale-95"
              >
                Start Learning Free
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/shs-dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 text-base font-semibold text-primary bg-white border border-primary/30 rounded-xl hover:bg-secondary transition-all active:scale-95"
              >
                <Icon name="AcademicCapIcon" size={18} />
                SHS Learning Hub
              </Link>
            </div>

            {/* Audience badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              {[
                { label: 'SHS Students', color: 'bg-green-100 text-green-700' },
                { label: 'Tertiary Students', color: 'bg-blue-100 text-blue-700' },
                { label: 'Professionals', color: 'bg-purple-100 text-purple-700' },
              ].map((badge) => (
                <span key={badge.label} className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
                  {badge.label}
                </span>
              ))}
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 pt-2">
              {floatingStats.map((stat) => (
                <div key={`hero-stat-${stat.label}`} className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-xl shadow-card border border-border">
                  <Icon name={stat.icon as never} size={18} className={stat.color} />
                  <div>
                    <p className="text-sm font-bold text-foreground tabular-nums">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: dynamic hero visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-purple bg-gradient-to-br from-primary/10 to-accent/10 aspect-[4/3]">
              {/* Skeleton loader shown while image loads */}
              {!imgLoaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 animate-pulse flex items-center justify-center">
                  <Icon name="PhotoIcon" size={48} className="text-primary/30" />
                </div>
              )}
              <Image
                src={heroImageSrc}
                alt={heroImageAlt}
                fill
                loading="lazy"
                className={`object-cover transition-opacity duration-500 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImgLoaded(true)}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>

            {/* Floating card */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-card-hover p-4 border border-border flex items-center gap-3 max-w-[200px]">
              <div className="w-10 h-10 gradient-card-green rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name="TrophyIcon" size={18} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Latest Achievement</p>
                <p className="text-sm font-bold text-foreground">Certificate Earned!</p>
              </div>
            </div>

            <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-card-hover p-4 border border-border">
              <div className="flex items-center gap-2 mb-1">
                {[1,2,3,4,5].map((s) => (
                  <Icon key={`star-${s}`} name="StarIcon" size={12} className="text-warning" variant="solid" />
                ))}
              </div>
              <p className="text-xs font-semibold text-foreground">4.9/5 Rating</p>
              <p className="text-xs text-muted-foreground">from 8,200+ reviews</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}